/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from "react";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";
import {
  accessAPI,
  Boton,
  Sidebar,
  TablaFiltrable,
  Titulo,
  tituloBase,
  formatFechaHora
} from "../../shared";
import VisorCorreo from "./VisorCorreo.js";

// Opción del selector de conversación para que la elija el asistente.
const QUE_ELIJA_EL_ASISTENTE = 0;

// Asignar y reintentar responden cuando el asistente terminó, que puede
// tardar bastante más que el timeout por defecto.
const OPCIONES_ASISTENTE = { timeout: 180000 };

/**
 * Bandeja de los correos de la casilla del historial que no se pudieron
 * asignar a un cliente, o cuyo procesamiento falló. Desde acá se asignan a
 * mano: el asistente los incorpora a la conversación elegida, o elige él
 * entre las abiertas del cliente.
 */
export default function CorreosSinAsignar() {
  const toast = useRef(null);
  const [correos, setCorreos] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [correoAbierto, setCorreoAbierto] = useState(null);
  const [correoAsignando, setCorreoAsignando] = useState(null);
  const [reprocesando, setReprocesando] = useState(null);

  function cargar() {
    accessAPI(
      "GET",
      "admin/comunicacioncliente/sinasignar",
      null,
      (respuesta) => setCorreos(respuesta),
      () => setCorreos([])
    );
  }

  useEffect(() => {
    document.title = "Correos sin asignar";
    cargar();
    accessAPI(
      "GET",
      "admin/comunicacioncliente/clientes",
      null,
      (respuesta) => setClientes(respuesta.map((c) => ({ value: c.id, label: c.razonsocial }))),
      () => {}
    );
    return () => (document.title = tituloBase());
  }, []);

  function reprocesar(correo) {
    setReprocesando(correo.id);
    accessAPI(
      "POST",
      `admin/comunicacioncliente/correo/${correo.id}/reprocesar`,
      null,
      () => {
        setReprocesando(null);
        cargar();
      },
      (respuesta) => {
        setReprocesando(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta?.error || "No se pudo reprocesar"
        });
      },
      undefined,
      undefined,
      OPCIONES_ASISTENTE
    );
  }

  const estadoTemplate = (correo) => {
    if (correo.posibleSpam) return <Tag value="Posible spam" severity="warning" />;
    if (correo.estado === "ERROR") return <Tag value="Error" severity="danger" />;
    if (correo.estado === "RECIBIDO") return <Tag value="Procesando" severity="info" />;
    return <Tag value="Sin asignar" severity="secondary" />;
  };

  const accionesTemplate = (correo) => (
    <div css={{ display: "flex", gap: "0.25em", justifyContent: "flex-end" }}>
      <Boton texto negro chico callback={() => setCorreoAbierto(correo.id)}>
        Ver
      </Boton>
      {correo.estado === "ERROR" && correo.clienteId && (
        <Boton
          texto
          negro
          chico
          loader={reprocesando === correo.id}
          callback={() => reprocesar(correo)}
        >
          Reintentar
        </Boton>
      )}
      {correo.estado !== "RECIBIDO" && (
        <Boton texto negro chico callback={() => setCorreoAsignando(correo)}>
          Asignar
        </Boton>
      )}
    </div>
  );

  return (
    <>
      <Toast ref={toast} />
      <Titulo>Correos sin asignar</Titulo>
      <TablaFiltrable
        value={correos || []}
        loading={correos === null}
        searchFields={["de", "asunto"]}
        nombreArchivo="correos_sin_asignar"
        emptyMessage="No hay correos sin asignar."
        size="small"
      >
        <Column
          header="Recibido"
          body={(c) => formatFechaHora(c.fechaRecepcion)}
          exportValue={(c) => formatFechaHora(c.fechaRecepcion)}
        />
        <Column header="De" field="de" />
        <Column header="Asunto" body={(c) => c.asunto || "(sin asunto)"} exportValue={(c) => c.asunto || ""} />
        <Column header="Estado" body={estadoTemplate} />
        <Column body={accionesTemplate} />
      </TablaFiltrable>

      <Sidebar
        visible={!!correoAbierto}
        position="right"
        onHide={() => setCorreoAbierto(null)}
        style={{ width: "min(60rem, 100vw)" }}
      >
        {correoAbierto && <VisorCorreo correoId={correoAbierto} toast={toast} />}
      </Sidebar>

      <Sidebar
        visible={!!correoAsignando}
        position="right"
        onHide={() => setCorreoAsignando(null)}
        className="w-full lg:w-30rem md:w-30rem"
      >
        {correoAsignando && (
          <AsignarCorreo
            correo={correoAsignando}
            clientes={clientes}
            toast={toast}
            onAsignado={() => {
              setCorreoAsignando(null);
              cargar();
            }}
          />
        )}
      </Sidebar>
    </>
  );
}

function AsignarCorreo({ correo, clientes, toast, onAsignado }) {
  const [clienteId, setClienteId] = useState(null);
  const [conversaciones, setConversaciones] = useState([]);
  const [conversacionId, setConversacionId] = useState(QUE_ELIJA_EL_ASISTENTE);
  const [asignando, setAsignando] = useState(false);

  // Si el remitente ya es contacto de un cliente, se lo propone.
  useEffect(() => {
    accessAPI(
      "GET",
      `admin/comunicacioncliente/correo/${correo.id}/sugerencia`,
      null,
      (respuesta) => respuesta.clienteId && setClienteId(respuesta.clienteId),
      () => {}
    );
  }, [correo.id]);

  useEffect(() => {
    setConversacionId(QUE_ELIJA_EL_ASISTENTE);
    setConversaciones([]);
    if (!clienteId) return;
    accessAPI(
      "GET",
      `admin/comunicacioncliente/cliente/${clienteId}/abiertas`,
      null,
      (respuesta) => setConversaciones(respuesta),
      () => {}
    );
  }, [clienteId]);

  function asignar() {
    setAsignando(true);
    accessAPI(
      "PUT",
      `admin/comunicacioncliente/correo/${correo.id}/asignar`,
      { clienteId, conversacionId: conversacionId || null },
      (respuesta) => {
        setAsignando(false);
        toast.current?.show(
          respuesta.estado === "PROCESADO"
            ? { severity: "success", summary: "Asignado", detail: "El correo se incorporó a la conversación" }
            : {
                severity: "warn",
                summary: "Asignado con error",
                detail: respuesta.errorProcesamiento || "El asistente no pudo procesarlo"
              }
        );
        onAsignado();
      },
      (respuesta) => {
        setAsignando(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta?.error || "No se pudo asignar"
        });
      },
      undefined,
      undefined,
      OPCIONES_ASISTENTE
    );
  }

  const opcionesConversacion = [
    { value: QUE_ELIJA_EL_ASISTENTE, label: "Que la elija el asistente" },
    ...conversaciones.map((c) => ({ value: c.id, label: c.titulo }))
  ];

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "0.75em" }}>
      <h3 css={{ marginTop: 0 }}>Asignar correo</h3>
      <div>
        <strong>{correo.asunto || "(sin asunto)"}</strong>
        <div css={{ fontSize: "0.85em", opacity: 0.7 }}>{correo.de}</div>
      </div>
      <label htmlFor="asignar-cliente">Cliente</label>
      <Dropdown
        inputId="asignar-cliente"
        value={clienteId}
        options={clientes}
        onChange={(e) => setClienteId(e.value)}
        filter
      />
      <label htmlFor="asignar-conversacion">Conversación</label>
      <Dropdown
        inputId="asignar-conversacion"
        value={conversacionId}
        options={opcionesConversacion}
        onChange={(e) => setConversacionId(e.value)}
        disabled={!clienteId}
      />
      <Boton verde loader={asignando} disabled={!clienteId} callback={asignar}>
        Asignar
      </Boton>
    </div>
  );
}
